import React, { useCallback, useState } from "react";
import { useMutation, useQuery } from "react-query";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";
import { ValidationContext, ValidationContextProps } from "./validation-context-props.ts";
import useProjectContext from "../../../../../../services/context/project/use-project-context.ts";
import { fetchItem, putItem } from "../../../../../../queries/queries-helper.ts";
import ENDPOINTS from "../../../../../../services/endpoints.ts";
import { PackDto } from "../../../../../../models/interfaces/dtos/pack-dto.ts";
import { FileRevisionDto } from "../../../../../../models/interfaces/dtos/file-revision-dto.ts";
import ValidationPack from "../../models/validation-pack.ts";
import ValidationState from "../../../../../../models/enums/validation-state.ts";
import useImmerReducer from "../../../../../../hooks/use-immer-reducer.ts";
import validationReducer from "./validation-reducer.ts";
import ValidationReducerAction from "./validation-reducer-action.ts";
import SimplifiedFilterDto from "@/models/interfaces/dtos/simplified-filter-dto.ts";
import ShellValidationDTO from "@/models/interfaces/dtos/shell-validation-dto.ts";
import PackData from "../../layouts/details/models/pack-data.ts";
import WorkspaceFilesDto from "@/models/interfaces/dtos/workspace-files-dto.ts";
import _3DRecord from "../../layouts/details/models/_3d-record.ts";
import SimpleLoadingIndicator from "@/components/simple-loading-indicator/index.tsx";
import { normalizeViewerObjectId, parseViewerObjectId } from "@/utils/viewer-object-identity.ts";

const ValidationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const { ensureCanEdit } = useProjectEditAccess();
    const { project } = useProjectContext();

    const [data, dispatch] = useImmerReducer<ValidationPack[], ValidationReducerAction>(
        validationReducer,
        []
    );

    const [packsData, setPacksData] = useState<Map<string, PackData> | undefined>(undefined);

    const { isLoading: isPacksLoading, refetch: refetchPacks } = useQuery(
        ['packs-baamer-file-revision', project.baamerRevisionId],
        async () => {
            const fetchedData = await fetchItem<PackDto[]>(ENDPOINTS.PACK(project.baamerRevisionId)) ?? [];
            
            const validationPacks: ValidationPack[] = [];
            const _packsData = new Map<string, PackData>();
            
            await Promise.all(
                fetchedData.map(async pack => {
                    validationPacks.push(new ValidationPack(pack));

                    const workspaceFilesDto = await fetchItem<WorkspaceFilesDto>(
                        ENDPOINTS.PROJECT_3D_FILES(project.id, pack.storeyName)
                    );
                    const files3D = workspaceFilesDto?.workspaceFilesPaths
                        .flatMap(element => element.filePaths)
                        .filter(fileElement => {
                            const extension = fileElement.extension?.toLowerCase();
                            return extension === '.3drtgm' || extension === '.3dtgm';
                        })
                        .map(fileElement =>
                            new _3DRecord(fileElement.name, fileElement.path)
                        ) ?? [];
                    _packsData.set(
                        pack.packId,
                        {
                            files3D,
                            objectTypos: []
                        }
                    );

                    const simplifiedFilterDto = await fetchItem<SimplifiedFilterDto>(
                        ENDPOINTS.PACK_FILTER_NODE(pack.packId)
                    );
                    const filteredMetaObjects = simplifiedFilterDto?.filteredNodes?.at(0)?.filteredMetaObjects;
                    if (!filteredMetaObjects) return;

                    const shellValidationDtos = await fetchItem<ShellValidationDTO[]>(ENDPOINTS.SHELL_VALIDATION_GET_BY_VALIDATION(pack.validationId));
                    if (!shellValidationDtos) return;
                    
                    filteredMetaObjects.forEach(fmo => {
                        const objectId = normalizeViewerObjectId(fmo);
                        const formattedObject = parseViewerObjectId(objectId);

                        if (objectId === undefined || formattedObject === undefined) {
                            return;
                        }

                        const found = shellValidationDtos.find(shell =>
                            objectId === normalizeViewerObjectId(shell._3D)
                        );
                        if (!found) return;

                        const shellTypo = (found.type == null) ? found.defaultType : found.type;
                        const thermalTypo = (found.thermalType == null) ? found.defaultThermalType : found.thermalType;

                        const pd = _packsData.get(pack.packId);
                        if (!pd) return;

                        pd.objectTypos.push({
                            shellId: found.shellValidationId,
                            _3D: objectId,
                            object: formattedObject,
                            shellTypo: shellTypo,
                            thermalTypo: thermalTypo,
                        });
                        _packsData.set(pack.packId, pd);
                    });
                })
            );
            
            dispatch({ type: 'populate', payload: validationPacks });
            setPacksData(_packsData);
            return validationPacks;
        },
    );

    const { isLoading: isFilesLoading, data: filesData, refetch: refetchFiles } = useQuery(
        ['files-revision-baamer-revision', project.baamerRevisionId],
        async () => (await fetchItem<FileRevisionDto[]>(ENDPOINTS.FILE_REVISION(project.baamerRevisionId)) ?? undefined),
    );

    const refreshData = useCallback(async () => {
        await Promise.all([
            refetchPacks({ throwOnError: true, cancelRefetch: true }),
            refetchFiles({ throwOnError: true, cancelRefetch: true })
        ]);
    }, [refetchPacks, refetchFiles]);

    const acceptPackMutation = useMutation({
        mutationFn: (validationId: string) => putItem(ENDPOINTS.VALIDATION_SET_NEW_STATE(validationId, ValidationState.Validated, true), null)
    });

    const refusePackMutation = useMutation({
        mutationFn: (validationId: string) => putItem(ENDPOINTS.VALIDATION_SET_NEW_STATE(validationId, ValidationState.Refused, true), null)
    });

    const acceptPack = useCallback(async (packId: string, validationId: string) => {
        if (!ensureCanEdit()) {
            return;
        }

        await acceptPackMutation.mutateAsync(validationId).then(() => {
            dispatch({ type: 'updateValidation', packId, validationId, state: ValidationState.Validated });
        })
    }, [ensureCanEdit, acceptPackMutation, dispatch]);

    const refusePack = useCallback(async (packId: string, validationId: string) => {
        if (!ensureCanEdit()) {
            return;
        }

        await refusePackMutation.mutateAsync(validationId).then(() => {
            dispatch({ type: 'updateValidation', packId, validationId, state: ValidationState.Refused });
        });
    }, [ensureCanEdit, refusePackMutation, dispatch]);

    const isLoading = isPacksLoading || isFilesLoading || packsData == null;

    if (isLoading)
        return <SimpleLoadingIndicator />

    const contextProps: ValidationContextProps = {
        isLoading,        

        validationPacks: data,
        fileRevisions: filesData,
        packsData: packsData,

        acceptPack,
        refusePack,

        refreshData
    }

    return (
        <ValidationContext.Provider value={contextProps}>
            {children}
        </ValidationContext.Provider>
    )
}

export default ValidationProvider;
